import { useHistory } from 'react-router-dom';

import { ChakraFlex, ChakraText, FAIconName, Icon } from '@equip.health/ui';

import { Path } from '~/routes/constants';
import {
  formatPauseSince,
  getDaysSinceLabel,
} from '../provider-dashboard.util';
import {
  PatientProviderDashboard,
  VIEW_DETAIL_TYPE,
} from '~/store/services/providerDashboard';
import PatientIdentity from './PatientIdentity';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { ProviderDashboardEvents } from '~/analytics/providerDashboardEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

interface PatientRowProps {
  patient: PatientProviderDashboard;
  tabKey?: string;
}

const PausedInfoLine = ({
  reason,
  startDate,
  patientExternalId,
}: {
  reason: string;
  startDate: string;
  patientExternalId: string;
}) => (
  <ChakraText
    data-testid={`patient-pause-detail-${patientExternalId}`}
    fontSize="12px"
    color="neutral.secondary"
    marginTop="4px"
  >
    {`Paused since ${formatPauseSince(startDate)}${reason ? ` · ${reason}` : ''}`}
  </ChakraText>
);

const PatientRow = ({ patient, tabKey }: PatientRowProps) => {
  const history = useHistory();
  const { trackEvent } = useAnalytics() ?? {};

  const { patientExternalId, viewDetail } = patient;

  const pauseDetail =
    viewDetail?.type === VIEW_DETAIL_TYPE.PAUSE ? viewDetail : null;

  const handleClick = () => {
    trackEvent?.(ProviderDashboardEvents.myPatientsPatientClicked, {
      tabKey,
      Source: AnalyticsSourceValues.PROVIDER_DASHBOARD,
    });
    history.push(`${Path.MAUD_PATIENTS}/${patientExternalId}`);
  };

  return (
    <ChakraFlex
      alignItems="center"
      borderTop="1px solid"
      borderColor="neutral.line"
      cursor="pointer"
      data-testid={`patient-row-${patientExternalId}`}
      gap="12px"
      onClick={handleClick}
      padding="14px 0"
      _hover={{ bg: 'neutral.background.secondary' }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <PatientIdentity
        patient={patient}
        footer={
          pauseDetail && (
            <PausedInfoLine
              reason={pauseDetail.reason}
              startDate={pauseDetail.startDate}
              patientExternalId={patientExternalId}
            />
          )
        }
      />

      {pauseDetail && (
        <ChakraFlex
          data-testid={`patient-pause-days-${patientExternalId}`}
          flexShrink={0}
          alignItems="center"
          gap="4px"
          border="1px solid"
          borderColor="neutral.line"
          borderRadius="6px"
          padding="4px 8px"
          bg="neutral.background.primary"
          whiteSpace="nowrap"
        >
          <Icon
            name={FAIconName.calendarXmarkRegular}
            color="neutral.primary"
            size="sm"
          />
          <ChakraText
            fontSize="13px"
            fontWeight="500"
            letterSpacing="-0.39px"
            lineHeight="1.4"
            color="neutral.primary"
          >
            {getDaysSinceLabel(pauseDetail.startDate)}
          </ChakraText>
        </ChakraFlex>
      )}
    </ChakraFlex>
  );
};

export default PatientRow;
